/**
 * hiveRoutes.tsx — hive detail + fleet board routes (all authenticated users)
 * Extracted from App.tsx in #2470 (pure refactor, no behaviour changes).
 *
 * Covers: /hives, /hives/:projectName/*, /fleet, /fleet/board,
 *         /all-hives (platform-admin), /servers
 */
import React, { lazy, Suspense } from "react";
import { Route, Navigate } from "react-router-dom";
import type { PageFallbackProps, ProtectedComponent, PlatformAdminProtectedComponent } from "./types";

// Lazy-loaded hive + fleet pages
const AdminFleetBoard  = lazy(() => import("../pages/AdminFleetBoard").then(m => ({ default: m.AdminFleetBoard })));
const AdminProjects    = lazy(() => import("../pages/AdminProjects").then(m => ({ default: m.AdminProjects })));
const AdminAllHives    = lazy(() => import("../pages/AdminAllHives").then(m => ({ default: m.AdminAllHives })));
const AdminServers     = lazy(() => import("../pages/AdminServers").then(m => ({ default: m.AdminServers })));
const AdminProjectDetail = lazy(() => import("../pages/AdminProjectDetail").then(m => ({ default: m.AdminProjectDetail })));
const HiveFilesPage    = lazy(() => import("../pages/HiveFilesPage").then(m => ({ default: m.HiveFilesPage })));
// #2321 — 90-second hive snapshot replay tour
const HiveReplayPage   = lazy(() => import("../pages/HiveReplayPage").then(m => ({ default: m.HiveReplayPage })));
const AiderChatPage    = lazy(() => import("../pages/AiderChatPage").then(m => ({ default: m.AiderChatPage })));
// #3117 — per-hive API keys admin (sk-2hi- prefix, 365-day expiry)
const HiveApiKeysPage  = lazy(() => import("../pages/HiveApiKeysPage").then(m => ({ default: m.HiveApiKeysPage })));

interface Props {
  PageFallback: React.ComponentType<PageFallbackProps>;
  Protected: ProtectedComponent;
  PlatformAdminProtected: PlatformAdminProtectedComponent;
  ChatRedirect: React.ComponentType;
}

/** Returns the array of hive + fleet-board <Route> elements. */
export function getHiveRoutes({
  PageFallback,
  Protected,
  PlatformAdminProtected,
  ChatRedirect,
}: Props): React.ReactElement[] {
  const F = <PageFallback />;

  return [
    // Fleet board
    <Route key="fleet-redir"  path="/fleet"       element={<Navigate to="/fleet/board" replace />} />,
    <Route key="fleet-board"  path="/fleet/board" element={
      <Protected><Suspense fallback={F}><AdminFleetBoard /></Suspense></Protected>
    } />,

    // Hive list
    <Route key="hives"        path="/hives" element={
      <Protected><Suspense fallback={F}><AdminProjects /></Suspense></Protected>
    } />,

    // Hive detail — primary routes
    <Route key="hive-detail"  path="/hives/:projectName" element={
      <Protected><Suspense fallback={F}><AdminProjectDetail /></Suspense></Protected>
    } />,
    // /hives/:projectName/chat redirects to detail with chat=1
    <Route key="hive-chat"    path="/hives/:projectName/chat" element={
      <Protected><ChatRedirect /></Protected>
    } />,
    // Files panel — #2176
    <Route key="hive-files"   path="/hives/:projectName/files" element={
      <Protected><Suspense fallback={F}><HiveFilesPage /></Suspense></Protected>
    } />,
    // Replay tour — #2321
    <Route key="hive-replay"  path="/hives/:name/replay" element={
      <Protected><Suspense fallback={F}><HiveReplayPage /></Suspense></Protected>
    } />,
    // Per-hive API keys admin (#3117). Scoped per-hive per founder per-hive
    // scope decision on parent #3099. Backend routes:
    //   GET    /admin/hives/{slug}/api-keys
    //   POST   /admin/hives/{slug}/api-keys
    //   DELETE /admin/hives/{slug}/api-keys/{id}
    //   POST   /admin/hives/{slug}/api-keys/{id}/rotate
    <Route key="hive-apikeys" path="/hives/:projectName/api-keys" element={
      <Protected><Suspense fallback={F}><HiveApiKeysPage /></Suspense></Protected>
    } />,

    // Tenant environment view — all authenticated fleet users
    <Route key="servers"      path="/servers" element={
      <Protected><Suspense fallback={F}><AdminServers /></Suspense></Protected>
    } />,

    // All-hives admin grid — platform-admin only (#1446 + #1447)
    <Route key="all-hives"    path="/all-hives" element={
      <PlatformAdminProtected><Suspense fallback={F}><AdminAllHives /></Suspense></PlatformAdminProtected>
    } />,
  ];
}
